/* eslint-disable react/require-default-props */
import { Select, Spin } from 'antd'
import type { SelectProps } from 'antd/es/select'
import debounce from 'lodash/debounce'
import React, { useMemo, useRef, useState } from 'react'

export interface DebounceSelectProps<ValueType = any>
  extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
  fetchOptions: (search: string) => Promise<ValueType[]>
  debounceTimeout?: number
  empty?: string
}

function DebounceSelect<
  ValueType extends {
    key?: string
    label: React.ReactNode
    value: string | number
    name?: string
    empty?: string
  } = any
>({ fetchOptions, debounceTimeout = 0, empty, ...props }: DebounceSelectProps<ValueType>) {
  const [fetching, setFetching] = useState(false)
  const [options, setOptions] = useState<ValueType[]>([])
  const fetchRef = useRef(0)

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      fetchRef.current += 1
      const fetchId = fetchRef.current
      setOptions([])
      setFetching(true)

      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          return
        }

        setOptions(newOptions)
        setFetching(false)
      })
    }

    return debounce(loadOptions, debounceTimeout)
  }, [fetchOptions, debounceTimeout])

  return (
    <Select
      labelInValue
      filterOption={false}
      onFocus={() => debounceFetcher('')}
      onSearch={debounceFetcher}
      placeholder='Digite para buscar'
      notFoundContent={
        fetching ? <Spin size='small' /> : options.length === 0 ? <>{empty} não encontrado</> : null
      }
      {...props}
    >
      {options.map((item) => (
        <Select.Option key={item.value} value={item.value}>
          {item.label}
        </Select.Option>
      ))}
    </Select>
  )
}

interface UserValue {
  label: string
  value: number
  name?: string
}

interface TypesSelectSearch {
  fetchOptions: (search: string) => Promise<UserValue[]>
  // eslint-disable-next-line react/require-default-props
  onChange?: (values: UserValue) => void
  // eslint-disable-next-line react/require-default-props
  disabled?: boolean
  empty?: string
}

const SelectSearch: React.FC<TypesSelectSearch> = ({
  fetchOptions,
  onChange,
  empty,
  disabled = false,
}) => {
  const [value, setValue] = useState<UserValue>()

  return (
    <DebounceSelect
      disabled={disabled}
      allowClear
      empty={empty}
      showSearch
      value={value}
      fetchOptions={(s) => fetchOptions(s)}
      onChange={(newValue) => {
        onChange(newValue as UserValue)
        setValue(newValue as UserValue)
      }}
      style={{ width: '100%' }}
    />
  )
}

export default SelectSearch
